import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../assets/styles/EditForm.scss';
import { getAllCustomFields } from '../services/customFieldServices';
import { getAllUsers } from '../services/userServices';
import { useSettings } from '../context/SettingsContext';
import { formatStatusLabel } from '../utils/statusFormatter';
import { getGlobalDateFormat, parseDateInput, toInputDateValue } from '../utils/dateFormatter';
import CrmTabs from './ui/CrmTabs';
import { CrmCheckbox, CrmInput, CrmSelect, CrmTextarea, CrmDateInput, CrmTimeInput } from './ui/CrmFormControls';

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh',
  'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
  'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi'
];

const statusOptions = ['new', 'in-progress', 'closed', 'won', 'lost'];
const priorityOptions = ['low', 'medium', 'high'];

const fieldChange = (handleChange, name) => (eventOrValue) => {
  const value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
  handleChange({ target: { name, value } });
};

const timeFromDateTime = (value) => {
  const parsed = parseDateInput(value);
  if (!parsed) return '';
  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
};

const cleanNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  return Number.isFinite(number) ? String(number) : String(value);
};

const combineDateTime = (currentValue, nextDate, nextTime) => {
  const currentDate = toInputDateValue(currentValue);
  const currentTime = timeFromDateTime(currentValue);
  const datePart = nextDate ?? currentDate;
  const timePart = nextTime ?? currentTime;
  if (!datePart) return '';
  return timePart ? `${datePart}T${timePart}` : datePart;
};

function CustomField({ field, value, onChange, dateFormat }) {
  const common = {
    id: `custom-field-${field.field_id}`,
    label: field.field_name,
    value: value ?? '',
    onChange,
  };
  const type = String(field.field_type || 'text').toLowerCase();

  if (type === 'textarea') return <CrmTextarea {...common} rows={4} />;
  if (type === 'select' || type === 'dropdown') {
    return <CrmSelect {...common}><option value="">Select</option>{(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}</CrmSelect>;
  }
  if (type === 'date') return <CrmDateInput {...common} dateFormat={dateFormat || getGlobalDateFormat()} onChange={(next) => onChange({ target: { value: next } })} />;
  if (type === 'time') return <CrmTimeInput {...common} />;
  return <CrmInput {...common} type={['number', 'email', 'tel'].includes(type) ? type : 'text'} />;
}

const EditForm = ({
  leadData,
  handleChange,
  handleSubmit,
  customFields: initialCustomFields,
  handleCustomFieldsUpdate,
  activeTab,
  setActiveTab,
  hideActions = false,
}) => {
  const { settings } = useSettings();
  const isCateringBusiness = String(settings?.business_type || '').toUpperCase() === 'CATERING';
  const dateFormat = settings?.date_format || getGlobalDateFormat();
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [users, setUsers] = useState([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  useEffect(() => {
    let mounted = true;
    Promise.all([getAllUsers().catch(() => []), getAllCustomFields().catch(() => [])]).then(([userList, fields]) => {
      if (!mounted) return;
      const salesUsers = (Array.isArray(userList) ? userList : []).filter((user) => {
        const roleText = String(user.role_name || user.role || '').toLowerCase();
        return roleText.includes('salesman');
      });
      setUsers(salesUsers);
      setCustomFieldDefinitions(Array.isArray(fields) ? fields : []);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const sourceValues = Array.isArray(leadData?.custom_fields) && leadData.custom_fields.length
      ? leadData.custom_fields
      : (Array.isArray(initialCustomFields) ? initialCustomFields : []);
    if (!sourceValues.length) return;
    setFieldValues((current) => {
      const next = { ...current };
      sourceValues.forEach((entry) => { next[entry.field_id] = entry.field_value ?? ''; });
      return next;
    });
  }, [initialCustomFields, leadData?.custom_fields]);

  useEffect(() => {
    if (!sameAsBilling) return;
    ['address', 'landmark', 'city', 'state', 'pincode'].forEach((part) => {
      handleChange({ target: { name: `shipping_${part}`, value: leadData?.[`billing_${part}`] || '' } });
    });
  }, [sameAsBilling, handleChange, leadData?.billing_address, leadData?.billing_landmark, leadData?.billing_city, leadData?.billing_state, leadData?.billing_pincode]);

  const updateCustomField = useCallback((fieldId, value) => {
    setFieldValues((current) => {
      const next = { ...current, [fieldId]: value };
      handleCustomFieldsUpdate(Object.entries(next).map(([id, fieldValue]) => ({ field_id: Number(id), field_value: fieldValue })));
      return next;
    });
  }, [handleCustomFieldsUpdate]);

  const tabs = useMemo(() => [
    { id: 'contactDetails', label: 'Contact Details' },
    { id: 'addresses', label: 'Addresses' },
    { id: 'moreDetails', label: 'More Details' },
    ...(isCateringBusiness ? [{ id: 'eventDetails', label: 'Event Details' }] : []),
    ...(customFieldDefinitions.length ? [{ id: 'customFields', label: 'Custom Fields' }] : []),
  ], [customFieldDefinitions.length, isCateringBusiness]);

  const currentTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : 'contactDetails';
  useEffect(() => {
    if (currentTab !== activeTab) setActiveTab?.(currentTab);
  }, [activeTab, currentTab, setActiveTab]);

  const userOptions = users.map((user) => ({ value: user.id, label: user.name || user.email || `User #${user.id}` }));

  return (
    <form className="workspace-form" onSubmit={handleSubmit}>
      <CrmTabs items={tabs} activeId={currentTab} onChange={setActiveTab} ariaLabel="Contact form sections" className="workspace-form__tabs" />

      <div className="workspace-form__panel" role="tabpanel">
        {currentTab === 'contactDetails' && <>
          <div className="workspace-form__section-heading"><div><h2>Contact details</h2><p>Core identity and communication information for this contact.</p></div></div>
          <div className="workspace-form__grid">
            <CrmInput label="First name" name="first_name" value={leadData.first_name || ''} onChange={handleChange} />
            <CrmInput label="Last name" name="last_name" value={leadData.last_name || ''} onChange={handleChange} />
            <CrmInput label="Email" type="email" name="email" value={leadData.email || ''} onChange={handleChange} />
            <CrmInput label="Phone number" type="tel" name="phone_number" value={leadData.phone_number || ''} onChange={handleChange} />
            <CrmInput label="Company name" name="company_name" value={leadData.company_name || ''} onChange={handleChange} />
            <CrmInput label="Contact name" name="contact_name" value={leadData.contact_name || ''} onChange={handleChange} />
            <CrmInput label="GST number" name="gst_number" value={leadData.gst_number || ''} onChange={handleChange} />
            <CrmInput label="Source" name="source" value={leadData.source || ''} disabled />
          </div>
        </>}

        {currentTab === 'addresses' && <>
          <div className="workspace-form__section-heading"><div><h2>Addresses</h2><p>Billing and shipping information used across documents and fulfilment.</p></div></div>
          <div className="workspace-form__address-block">
            <h3>Billing address</h3>
            <div className="workspace-form__grid">
              <CrmTextarea className="workspace-form__span-2" label="Address" name="billing_address" value={leadData.billing_address || ''} onChange={handleChange} rows={3} />
              <CrmInput label="Landmark" name="billing_landmark" value={leadData.billing_landmark || ''} onChange={handleChange} />
              <CrmInput label="City" name="billing_city" value={leadData.billing_city || ''} onChange={handleChange} />
              <CrmSelect label="State" name="billing_state" value={leadData.billing_state || ''} onChange={handleChange}><option value="">Select state</option>{indianStates.map((state) => <option key={state} value={state}>{state}</option>)}</CrmSelect>
              <CrmInput label="Pincode" name="billing_pincode" value={leadData.billing_pincode || ''} onChange={handleChange} />
            </div>
          </div>
          <div className="workspace-form__address-block">
            <div className="workspace-form__address-title"><h3>Shipping address</h3><CrmCheckbox label="Same as billing" checked={sameAsBilling} onChange={(event) => setSameAsBilling(event.target.checked)} /></div>
            <div className="workspace-form__grid">
              <CrmTextarea className="workspace-form__span-2" label="Address" name="shipping_address" value={leadData.shipping_address || ''} onChange={handleChange} rows={3} disabled={sameAsBilling} />
              <CrmInput label="Landmark" name="shipping_landmark" value={leadData.shipping_landmark || ''} onChange={handleChange} disabled={sameAsBilling} />
              <CrmInput label="City" name="shipping_city" value={leadData.shipping_city || ''} onChange={handleChange} disabled={sameAsBilling} />
              <CrmSelect label="State" name="shipping_state" value={leadData.shipping_state || ''} onChange={handleChange} disabled={sameAsBilling}><option value="">Select state</option>{indianStates.map((state) => <option key={state} value={state}>{state}</option>)}</CrmSelect>
              <CrmInput label="Pincode" name="shipping_pincode" value={leadData.shipping_pincode || ''} onChange={handleChange} disabled={sameAsBilling} />
            </div>
          </div>
        </>}

        {currentTab === 'moreDetails' && <>
          <div className="workspace-form__section-heading"><div><h2>More details</h2><p>Sales ownership, pipeline state, follow-up and internal context.</p></div></div>
          <div className="workspace-form__grid">
            <CrmSelect label="Status" name="lead_status" value={leadData.lead_status || 'new'} onChange={handleChange}>{statusOptions.map((option) => <option key={option} value={option}>{formatStatusLabel(option)}</option>)}</CrmSelect>
            <CrmSelect label="Priority" name="priority" value={leadData.priority || 'medium'} onChange={handleChange}>{priorityOptions.map((option) => <option key={option} value={option}>{formatStatusLabel(option)}</option>)}</CrmSelect>
            <CrmDateInput label="Follow-up date" value={toInputDateValue(leadData.follow_up_date)} dateFormat={dateFormat} onChange={(value) => handleChange({ target: { name: 'follow_up_date', value: combineDateTime(leadData.follow_up_date, value, undefined) } })} />
            <CrmTimeInput label="Follow-up time" value={timeFromDateTime(leadData.follow_up_date)} onChange={(event) => handleChange({ target: { name: 'follow_up_date', value: combineDateTime(leadData.follow_up_date, undefined, event.target.value) } })} />
            <CrmSelect label="Assigned salesperson" name="assigned_salesperson" value={leadData.assigned_salesperson || ''} onChange={handleChange}><option value="">Unassigned</option>{userOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</CrmSelect>
            <CrmInput label="Hotness (1-5)" type="number" min="1" max="5" name="hotness" value={cleanNumericValue(leadData.hotness ?? 1)} onChange={handleChange} />
            <CrmInput label="Amount" type="number" step="0.01" name="amount" value={cleanNumericValue(leadData.amount ?? 0)} onChange={handleChange} />
            <CrmTextarea className="workspace-form__span-2" label="Notes" name="notes" value={leadData.notes || ''} onChange={handleChange} rows={5} />
          </div>
        </>}

        {currentTab === 'eventDetails' && <>
          <div className="workspace-form__section-heading"><div><h2>Event details</h2><p>Catering-specific event information linked to this contact.</p></div></div>
          <div className="workspace-form__grid">
            <CrmInput label="Event name" name="event_name" value={leadData.event_name || ''} onChange={handleChange} />
            <CrmInput label="PAX" type="number" min="0" name="pax" value={cleanNumericValue(leadData.pax)} onChange={handleChange} />
            <CrmInput className="workspace-form__span-2" label="Event venue" name="event_location" value={leadData.event_location || ''} onChange={handleChange} />
            <CrmDateInput label="Event start date" value={leadData.event_start_date || ''} dateFormat={dateFormat} onChange={fieldChange(handleChange, 'event_start_date')} />
            <CrmTimeInput label="Event start time" name="event_start_time" value={leadData.event_start_time || ''} onChange={handleChange} />
            <CrmDateInput label="Event end date" value={leadData.event_end_date || ''} dateFormat={dateFormat} onChange={fieldChange(handleChange, 'event_end_date')} />
            <CrmTimeInput label="Event end time" name="event_end_time" value={leadData.event_end_time || ''} onChange={handleChange} />
          </div>
        </>}

        {currentTab === 'customFields' && <>
          <div className="workspace-form__section-heading"><div><h2>Custom fields</h2><p>Additional fields configured for contacts in CRM settings.</p></div></div>
          <div className="workspace-form__grid">
            {customFieldDefinitions.map((field) => <CustomField key={field.field_id} field={field} value={fieldValues[field.field_id]} dateFormat={dateFormat} onChange={(event) => updateCustomField(field.field_id, event.target.value)} />)}
          </div>
        </>}
      </div>

      {!hideActions && <div className="workspace-form__footer"><button type="submit" className="hs-listing__create">{leadData?.id ? 'Save changes' : 'Create contact'}</button></div>}
    </form>
  );
};

export default EditForm;
